/**
 * Display UTC database timestamps in the user's local timezone.
 * Database values are always UTC; never convert before persisting.
 */

const LEGACY_CREATED_KEYS = ['createdon_timestamp', 'created_at', 'uploaded_at', 'matched_at'];
const LEGACY_MODIFIED_KEYS = ['modifiedon_timestamp', 'updated_at'];

/**
 * Parse a UTC timestamp from API/DB (ISO or SQLite "YYYY-MM-DD HH:MM:SS").
 * @param {string|number|Date|null|undefined} value
 * @returns {Date|null}
 */
export function parseUtcTimestamp(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const raw = String(value).trim();
  if (!raw) return null;

  if (/^\d{4}-\d{2}-\d{2}T/.test(raw)) {
    const iso = raw.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(raw) ? raw : `${raw}Z`;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (/^\d{4}-\d{2}-\d{2}( \d{2}:\d{2}:\d{2})?/.test(raw)) {
    const d = new Date(`${raw.replace(' ', 'T')}Z`);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function readCreatedOn(record) {
  if (!record) return null;
  for (const key of LEGACY_CREATED_KEYS) {
    if (record[key] != null && record[key] !== '') return record[key];
  }
  return null;
}

export function readModifiedOn(record) {
  if (!record) return null;
  for (const key of LEGACY_MODIFIED_KEYS) {
    if (record[key] != null && record[key] !== '') return record[key];
  }
  return readCreatedOn(record);
}

/**
 * Format UTC timestamp for display in the browser's local timezone.
 */
export function formatLocalDateTime(value, options) {
  const d = parseUtcTimestamp(value);
  if (!d) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...options,
  });
}

export function formatLocalDate(value, options) {
  const d = parseUtcTimestamp(value);
  if (!d) return '—';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  });
}

/**
 * Parse a DATE-only value (`YYYY-MM-DD`) as a local calendar date.
 * Date columns carry no time zone, so appending `Z` would shift them a day
 * for users west of UTC.
 * @param {string|number|Date|null|undefined} value
 * @returns {Date|null}
 */
export function parseDateOnly(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const raw = String(value).trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (match) {
    const d = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return parseUtcTimestamp(raw);
}

/** Format a DATE-only column without timezone drift. */
export function formatDateOnly(value, options) {
  const d = parseDateOnly(value);
  if (!d) return '—';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  });
}

/** Today's local calendar date as `YYYY-MM-DD` (toISOString would use UTC). */
export function localTodayIsoDate(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function formatLocalTime(value, options) {
  const d = parseUtcTimestamp(value);
  if (!d) return '—';
  return d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    ...options,
  });
}

export function formatRecordCreatedOn(record, options) {
  return formatLocalDateTime(readCreatedOn(record), options);
}

export function formatRecordModifiedOn(record, options) {
  return formatLocalDateTime(readModifiedOn(record), options);
}

export function timestampSortValue(valueOrRecord) {
  const raw =
    valueOrRecord && typeof valueOrRecord === 'object'
      ? readCreatedOn(valueOrRecord)
      : valueOrRecord;
  const d = parseUtcTimestamp(raw);
  return d ? d.getTime() : 0;
}
